import React, { useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { SceneTemplate } from '@/model';
import { clickFromKeyboard } from '@/util/keyboardUtil';
import { useScrollControl } from '@/hook/useScrollControl';
import { Popover, PopoverButton, PopoverItem, PopoverMenu } from '@/component/Popover';
import { VirtualList, VirtualListRef } from '@/component/VirtualList';
import { DeleteIcon, PasteIcon } from '@/assets/svgs/icons';


interface TemplateListProps {
    templates: SceneTemplate[];
    selectedId?: number;
    modifiedTemplates: { [key: number]: SceneTemplate };
    invalidTemplateIds: number[];
    onClickItem: (templateId: number) => void;
    onDuplicate: (templateId: number) => Promise<void>;
    onDelete: (templateId: number) => void;
    isSubmitting: boolean;
}

const TemplateList: React.FC<TemplateListProps> = (props) => {
    const {
        templates,
        selectedId,
        modifiedTemplates,
        invalidTemplateIds,
        onClickItem,
        onDuplicate,
        onDelete,
        isSubmitting
    } = props;

    const intl = useIntl();

    const virtualListRef = useRef<VirtualListRef>(null);
    const { setAutoScrollId, scrollTo } = useScrollControl(templates, virtualListRef);

    useEffect(() => {
        scrollTo(selectedId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId]);

    const renderTemplateItem = useCallback((template: SceneTemplate) => {
        const name = (modifiedTemplates[template.id] ?? template).name;
        return (
            <div role="listitem"
                 tabIndex={0}
                 aria-label={name}
                 aria-current={selectedId === template.id ? true : undefined}
                 className={classNames(
                     "template-item",
                     modifiedTemplates[template.id] && "modified",
                     selectedId === template.id && "selected",
                     invalidTemplateIds.includes(template.id) && "invalid"
                 )}
                 onClick={() => {
                     setAutoScrollId(template.id);
                     onClickItem(template.id);
                 }}
                 onKeyDown={clickFromKeyboard}
            >
                <div className="flex justify-between items-center">
                    <div className="template-item-label">
                        {name}
                    </div>
                    <Popover className="flex-none">
                        {({ open, close }) => (
                            <>
                                <PopoverButton open={open}
                                               close={close}
                                               aria-label={intl.formatMessage({ id: "i03010" })}
                                />
                                <PopoverMenu anchor="right start">
                                    <PopoverItem className="template-popover-item"
                                                 disabled={isSubmitting}
                                                 onClick={() => onDuplicate(template.id)}
                                    >
                                        <PasteIcon className="size-4" />
                                        {intl.formatMessage({ id: "i03005" })}
                                    </PopoverItem>
    
                                    <PopoverItem className="template-popover-item"
                                                 disabled={isSubmitting}
                                                 onClick={() => onDelete(template.id)}
                                    >
                                        <DeleteIcon className="size-4 text-point-red" />
                                        {intl.formatMessage({ id: "i03006" })}
                                    </PopoverItem>
                                </PopoverMenu>
                            </>
                        )}
                    </Popover>
                </div>
            </div>
        );
    }, [
        modifiedTemplates, selectedId, invalidTemplateIds, isSubmitting, intl,
        onClickItem, onDuplicate, onDelete, setAutoScrollId
    ]);

    if (templates.length === 0 || !selectedId) {
        return null;
    }
    return (
        <VirtualList ref={virtualListRef}
                     className="template-list scroll-gap-container"
                     items={templates}
                     itemHeight={72}
                     gap={4}
                     visibleItems={10}
                     getItemKey={template => template.id}
                     renderItem={renderTemplateItem}
        />
    );
};

export default TemplateList;
